import React from 'react';
import * as clipboard from 'clipboard-polyfill';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import CopyIcon from 'assets/icons/copy_icon.svg?react';
import { createReferralKey, getReferralKey } from 'actions/referral';
import NeedAuthDialog from 'components/NeedAuthDialog';
import { isAuthenticated } from 'helpers/authToken';
import { addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';

const ButtonStyled = styled(Button)(() => ({
  color: 'rgba(82, 82, 82, 1)',
  padding: '6px 24px 6px 25px',
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500,
  boxShadow: 'none',
  marginRight: 20,
  borderRadius: 8 // Match mobile sticky button (sticky was unified to r=8)
}));

const SuccessMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500
}));

const CopyUrlButton = ({ actionText, authRequired = true, callBack }) => {
  const dispatch = useDispatch();
  const t = useTranslate('Profession');
  const [isCopied, setIsCopied] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);

  const isMobile = useSelector((state) => state.app.isMobile);

  const handleCopyUrl = React.useCallback(async () => {
    let stringToCopy = `${window.location.origin}${window.location.pathname}`;

    const appendReferral = async () => {
      setGenerating(true);

      let result = await dispatch(getReferralKey());

      if (!result) {
        result = await dispatch(createReferralKey());
      }

      setGenerating(false);

      stringToCopy = `${stringToCopy}?referral=${result?.referralCode}`;
    };

    if (!authRequired) {
      if (isAuthenticated()) {
        await appendReferral();
      }
    } else {
      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }
      await appendReferral();
    }

    clipboard.writeText(stringToCopy).then(() => {
      if (isMobile) {
        dispatch(addMessage(new Message(t('Copied'), 'success')));
        return;
      }

      setIsCopied(true);

      callBack && callBack();

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    });
  }, [dispatch, isMobile, authRequired, t, callBack]);

  return (
    <>
      <ButtonStyled
        endIcon={generating ? <CircularProgress size={20} /> : <CopyIcon />}
        onClick={handleCopyUrl}
        variant="contained"
        color="primary"
        aria-label={actionText || t('Share')}>
        {actionText || t('Share')}
      </ButtonStyled>
      {isCopied && !isMobile ? <SuccessMessage>{t('Copied')}</SuccessMessage> : null}
      <NeedAuthDialog handleClose={() => setNeedAuth(false)} needAuth={needAuth} />
    </>
  );
};

export default CopyUrlButton;
